import { ApiSearching, ApiParamsChanged, ForceReloadSource } from '@/constants/bus_event'
import nativeMsgUtil from '@/utils/native_msg_util'

/**
 * @module SearchMixin
 * @description 列表页查询专用的 mixin，提供一些基础方法，协助 DataSource 与 各个组件间的通讯
 * <p>适用场景：列表页、查询组件</p>
 */
export default {
  props: {
    /**
     * 前端URI默认的参数，用来显示的
     */
    uriDefaultParams: {
      type: Object,
      default: () => {
        return {}
      }
    }
  },
  data() {
    return {
      tableColumnDefaultSortOrders: ['descending', 'ascending', null] // element 默认是从升序开始，调整为从降序开始
    }
  },
  computed: {
    /**
     * 表格高度
     * 需要减去一般的框架顶部、统计、表头、分页、间隙
     *
     * @returns {number} 像素
     */
    tableMaxHeight() {
      if (nativeMsgUtil.isUnderMobile()) {
        // 移动设备下，避免多个滚动条，不好操作，表格不需要滚动
        return 10000
      }

      let normalLayoutHeight = 220
      let maxHeight = window.innerHeight - normalLayoutHeight

      let minHeight = 3000 // 设置足够长，避免双滚动条（但是会缺失了 fixed header 的特性）
      return maxHeight > minHeight ? maxHeight : minHeight
    }
  },
  methods: {
    /************* bus 通讯 ****************/
    /**
     * 时间范围参数变化后，发射信号通知API加载。date-range-button-group 专用
     *
     * @param {object} bus 通讯总线
     * @param {string} paramKey 事件范围参数
     * @param {string} range 时间范围，格式: 1|2
     * @param {object} source 事件触发的源信息
     */
    emitApiDateRangeParam(bus, paramKey, range, source) {
      if (source.isClick) {
        // 只有当主动查询的，才会请求 API
        let params = {}
        params[paramKey] = range
        this.emitApiParamsChanged(bus, params, 'DateRangeButtonGroup')
      }
    },
    /**
     * @function emitApiParamsChanged
     * @description 基于当前路由 和 参数，请求加载 API。
     * 只有参数变化了，才会触发API加载（主要是为了防止多重刷新）。
     *
     * @param {object} bus 查询通讯的总线
     * @param {object} params 参数。Example { name: 'Joe' }
     * @param {string} sourceName 来源的名字。用于 log trace 谁触发的查询
     * @param {object} options 其他选项
     * @param {boolean} options.clearOtherParams 是否清空 $route 的其他参数。默认为 false
     */
    emitApiParamsChanged(bus, params = {}, sourceName = '', options = { clearOtherParams: false }) {
      if (bus === null) return

      let query = options.clearOtherParams ? {} : { ...this.$route.query }

      // 若不是翻页操作，将页码改为1
      // if (query.page && !params.page) {
      //   params.page = 1
      // }

      Object.keys(params).forEach(paramKey => {
        let paramValue = params[paramKey]
        if (this.isEmptyObject(paramValue)) {
          delete query[paramKey]
        } else {
          query[paramKey] = paramValue
        }
      })
      bus.$emit(ApiParamsChanged, query, sourceName, options)
    },
    /**
     * @function emitApiReload
     * @description 发射Api重新加载信号
     * <br/>平时触发Api加载一般是需要发射参数变化的信号，特殊情况需要直接刷新，而不通过参数变更触发
     * <br/>适用场景：列表页的编辑、删除操作后，刷新列表数据
     *
     * @param {object} bus 查询组件间通讯的总线
     */
    emitApiReload(bus) {
      // 增加一个特殊的参数，用户强制刷新
      this.emitApiParamsChanged(bus, {}, ForceReloadSource)
    },
    /**
     * @callback apiSearchingProcessCallback
     * @description onApiSearching 方法的回调
     * @param {object} context 查询回调返回的内容
     * @param {object} context.params URL 参数<br/>{ keyword: '测试', sort: 'created_at|desc' }
     * @param {Array<object>} context.filters 用作查询的参数<br/>[{ key: 'keyword', value: '测试' }]
     * @param {object} context.sort 排序<br/>{sortBy: 'created_at', sortOrder: 'desc'}
     * @param {object} context.pagination 分页<br/>{page: 1, perPage: 20}
     * @param {Array<string>} context.updatedParamsKeys 与上次比较变化了的参数<br/>['updated_at']
     */
    /**
     * @function onApiSearching
     * @description 接收Api的开始查询信号
     * <br/>适用场景：查询组件
     * @example created() {
     * // 接收Api的开始查询信号，然后同步该查询组件的展示
     * this.onApiSearching(this._apiSearchingRender)
     * }
     * @param {object} bus 查询通讯的总线
     * @param {apiSearchingProcessCallback} apiSearchingProcess 处理的方法
     */
    onApiSearching(bus, apiSearchingProcess) {
      // 接听外部的 API 参数改变的请求，然后触发 API   请求
      bus.$on(ApiSearching, apiSearchingProcess)
    },
    /**
     * @function offApiSearching
     * @description 关闭接收Api的开始查询信号
     * <br/>不注销的话，开发环境会因为 hot-loading 导致重复执行 onApiSearching
     * @example destroy() {
     * // 关闭/注销 onApiSearching 注册的监听事件
     * this.offApiSearching(this._apiSearchingRender)
     * }
     * @param {object} bus 查询通讯的总线
     * @param {Function} apiSearchingProcess 处理的方法
     */
    offApiSearching(bus, apiSearchingProcess) {
      bus.$off(ApiSearching, apiSearchingProcess)
    },

    /************* filters ***************/
    /**
     * 查找 filter
     *
     * @param {Array<object>} filters 当前搜索的信息
     * @param {string} key 查找的 filter 的 key
     * @returns {?object} undefined 则表示没有找到
     *  格式 { key, value, display }
     *
     */
    findFilterByKey(filters, key) {
      return filters.find(filter => filter.key === key)
    },

    /**
     * 清空指定的查询。该方法会发射信号请求 API 更新
     *
     * @param {object} bus 查询通讯的总线
     * @param {string} filterKey 参数的名字
     * @param {string} sourceName 操作来源
     */
    clearFilter(bus, filterKey, sourceName = '') {
      let params = {}
      params[filterKey] = null
      this.emitApiParamsChanged(bus, params, sourceName)
    },

    /**
     * 删除所有查询。该方法会发射信号请求 API 更新
     *
     * @param {object} bus 查询通讯的总线
     * @param {Array} filters 筛选
     * @param {string} sourceName 操作来源
     */
    clearFilters(bus, filters, sourceName = '') {
      let params = this._filters2Params(filters)
      let paramKeys = Object.keys(params)
      paramKeys.forEach(paramKey => {
        params[paramKey] = null
      })
      this.emitApiParamsChanged(bus, params, sourceName)
    },

    /**
     * 将 filters 转化为 params
     *
     * @param {Array} filters 筛选项
     * @returns {object} 参数
     */
    _filters2Params(filters) {
      let params = {}
      filters.forEach(filter => {
        params[filter.key] = filter.value
      })
      return params
    },

    /**
     * 更新 URL。一般是在搜索条件变更后执行
     *
     * @param {object} params 参数
     */
    updateUrl(params) {
      // 如果没有参数时，获取默认参数
      if (!this.isEmptyObject(this.uriDefaultParams) && this.isEmptyObject(params)) {
        params = this.uriDefaultParams
      }
      // 更新 $route.query
      this.$router.replace({ path: this.$route.path, query: params })
      // 更新 url
      let url = this._fullUrl(params)
      window.history.replaceState(null, url, url)
    },
    /**
     * 获取完整的 url
     *
     * @param {object} params 参数
     * @returns {string} URL
     */
    _fullUrl(params) {
      let url = this.$route.path
      let query = []
      for (let property in params) {
        query.push(property + '=' + encodeURIComponent(params[property].toString()))
      }

      if (query.length > 0) {
        url = url + '?' + query.join('&')
      }
      return url
    },

    /************* display value ***************/
    /**
     * 展示数组参数，并国际化。
     *
     * @example enabled,disabled => 启用,禁用
     * @param {string} paramValue 参数值
     * @param {object} itemLocales items对应的国际化集合
     * @returns {string} 数组展示
     */
    displayArrayParam(paramValue, itemLocales) {
      return paramValue
        .split(',')
        .map(item => itemLocales[item])
        .join(',')
    },
    /**
     * 展示范围参数
     *
     * @example 1|2 => 1~2 （后期建议使用~-）
     * @param {object} paramValue 参数值
     * @returns {string} 范围展示
     */
    displayRangeParam(paramValue) {
      return paramValue.split('|').join('~')
    },
    /**
     * 判断某列是否要显示
     *
     * @param {Array<string>} visiableColumns 显示的字段
     * @param {string} columnName 需要判断的字段字段
     * @returns {boolean} 是否显示
     */
    showColumn(visiableColumns, columnName) {
      return visiableColumns.includes(columnName)
    },
    /************** el-table 专用 begin *******************/
    /**
     * Table column 筛选的选项
     *
     * @param {object} propLocales 属性国际化 如: { enabled: '可用的', disabled: '不可用的' }
     * @returns {Array<object>} table 专属的属性筛选项格式 [{ text: '可用的', value: 'enabled' }]
     */
    columnFiltersOptions(propLocales) {
      return Object.keys(propLocales).map(prop => {
        return { text: propLocales[prop], value: prop }
      })
    },
    /**
     * 列排序调整
     *
     * @param {object} sortParams 排序参数
     * @param {object} sortParams.column 列对象
     * @param {string} sortParams.prop 列属性名
     * @param {string} sortParams.order 排序 ascending/descending
     */
    handleColumnSortChange({ prop, order }) {
      if (order === null) {
        // 如果恢复不排序，则移除参数
        this.emitApiParamsChanged(this.bus, { sort_by: null, sort_order: null }, 'List::sort')
      } else {
        // 按指定进行排序
        this.emitApiParamsChanged(this.bus, { sort_by: prop, sort_order: order === 'ascending' ? 'asc' : 'desc' }, 'List::sort')
      }
    },
    /**
     * 当前默认的排序(主要控制刷新页面后的回显)
     *
     * @returns {object} sort object
     */
    currentDefaultSort() {
      let query = this.$route.query
      let sort_by = null
      let sort_order = null
      if (query.sort_by) {
        sort_by = query.sort_by
      }
      if (query.sort_order) {
        sort_order = query.sort_order === 'asc' ? 'ascending' : 'descending'
      }

      let defaultSorts = { prop: sort_by, order: sort_order }

      if (this.isEmptyObject(defaultSorts)) {
        // 设定列的排序从降序开始
        defaultSorts = { order: 'descending' }
      }

      return defaultSorts
    },
    /**
     * 列筛选
     *
     * @param {object} filters 筛选信息，格式: { payment: ['alipay', 'wechat_pay'] }
     */
    handleColumnFilterChange(filters) {
      let columnProps = Object.keys(filters)

      let paramFilters = {}
      columnProps.forEach(columnProp => {
        let values = filters[columnProp]
        paramFilters[columnProp] = values.join(',')
      })

      this.emitApiParamsChanged(this.bus, paramFilters, 'List::column_filter')
    }
    /************** el-table 专用 end *******************/
  }
}
